import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { EDateTypes } from "../../../../shared/oversight-core/enums/date-types";
import FilteringSidePanel from "../../../../shared/oversight-core/shared-components/filtering-side-panel/filtering-side-panel";
import AppDatePicker from "../../../../shared/oversight-core/ui-elements/app-date-picker/app-date-picker";
import { getDefaultDates } from "../../../../shared/oversight-core/utils/date-utils";
import styles from "./dashboard-filters.module.scss";

interface IProps {
  show: boolean;
  onClose: () => void;
  onFilter: (
    startDate: Date | undefined,
    endDate: Date | undefined,
    selectedDateType: EDateTypes,
  ) => void;
  onClearAll: () => void;
  defaultDateType?: EDateTypes;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
}

const periodUnits: Record<string, moment.unitOfTime.StartOf> = {
  [EDateTypes.DAILY]: "day",
  [EDateTypes.MONTHLY]: "month",
  [EDateTypes.YEARLY]: "year",
};

// The unit each tab picks, as the range errors name it.
const periodNames: Record<string, string> = {
  [EDateTypes.DAILY]: "date",
  [EDateTypes.MONTHLY]: "month",
  [EDateTypes.YEARLY]: "year",
};

// Compares whole periods: From has to come before To, so a reversed range and one where both ends
// are the same day, month or year are both refused, each with its own message.
const getPeriodRangeError = (
  startDate: Date,
  endDate: Date,
  dateType: EDateTypes,
): string | undefined => {
  const unit = periodUnits[dateType] ?? "day";
  const name = periodNames[dateType] ?? "date";
  const start = moment(startDate).startOf(unit);
  const end = moment(endDate).startOf(unit);

  if (start.isAfter(end)) {
    return `From ${name} cannot be later than To ${name}`;
  }
  if (start.isSame(end)) {
    return `From and To ${name} cannot be the same`;
  }
  return undefined;
};

const DashboardFilters = (props: IProps) => {
  const {
    show,
    onClose,
    onFilter,
    onClearAll,
    defaultDateType = EDateTypes.DAILY,
    defaultStartDate,
    defaultEndDate,
  } = props;

  const [selectedDateType, setSelectedDateType] =
    useState<EDateTypes>(defaultDateType);
  const [startDate, setStartDate] = useState(() => {
    return defaultStartDate || getDefaultDates(defaultDateType).start;
  });
  const [endDate, setEndDate] = useState(() => {
    return defaultEndDate || getDefaultDates(defaultDateType).end;
  });
  const [dateFormat, setDateFormat] = useState("dd-MMM-yyyy");

  useEffect(() => {
    if (show) {
      const defaultDates = getDefaultDates(defaultDateType);
      setStartDate(defaultStartDate || defaultDates.start);
      setEndDate(defaultEndDate || defaultDates.end);
      setSelectedDateType(defaultDateType);
    }
  }, [show, defaultStartDate, defaultEndDate, defaultDateType]);

  useEffect(() => {
    switch (selectedDateType) {
      case EDateTypes.DAILY:
        setDateFormat("dd-MMM-yyyy");
        break;
      case EDateTypes.MONTHLY:
        setDateFormat("MMMM-yyyy");
        break;
      case EDateTypes.YEARLY:
        setDateFormat("yyyy");
        break;
      default:
        setDateFormat("dd-MMM-yyyy");
        break;
    }
  }, [selectedDateType]);

  const dateTypes = [EDateTypes.DAILY, EDateTypes.MONTHLY, EDateTypes.YEARLY];

  const dateRangeError = getPeriodRangeError(
    startDate,
    endDate,
    selectedDateType,
  );

  return (
    <FilteringSidePanel
      isOpen={show}
      onClose={onClose}
      isFilterDisabled={!!dateRangeError}
      onFilter={() => {
        onFilter(startDate, endDate, selectedDateType);
      }}
      onClearAll={() => {
        const defaultDates = getDefaultDates(EDateTypes.DAILY);
        setStartDate(defaultDates.start);
        setEndDate(defaultDates.end);
        setSelectedDateType(EDateTypes.DAILY);
        onClearAll();
      }}
    >
      <div className="mt-4">
        <label className="font-size-14">Time</label>
        <Row className="justify-content-between px-3">
          {dateTypes.map((type, index) => {
            return (
              <Col
                key={index}
                onClick={() => {
                  if (selectedDateType !== type) {
                    setSelectedDateType(type as EDateTypes);
                    const defaultDates = getDefaultDates(type as EDateTypes);
                    setStartDate(defaultDates.start);
                    setEndDate(defaultDates.end);
                  }
                }}
                className={`col-4 mt-2 font-weight-500 font-size-12 py-2 cursor-pointer text-center ${
                  selectedDateType === type ? styles.selected : styles.button
                }`}
              >
                {type}
              </Col>
            );
          })}
        </Row>
        <Row className="mt-4 flex-column">
          <Col>
            <Row className="flex-column">
              <Col>
                <label className="font-size-14">From</label>
              </Col>
              <Col>
                <AppDatePicker
                  selectedDate={startDate}
                  onChange={(date: Date) => {
                    setStartDate(date);
                  }}
                  monthYearPicker={selectedDateType === EDateTypes.MONTHLY}
                  yearPicker={selectedDateType === EDateTypes.YEARLY}
                  dateFormat={dateFormat}
                  fontSize={12}
                />
              </Col>
            </Row>
          </Col>
          <Col className="mt-4">
            <Row className="flex-column">
              <Col>
                <label className="font-size-14">To</label>
              </Col>
              <Col>
                <AppDatePicker
                  selectedDate={endDate}
                  onChange={(date: Date) => {
                    setEndDate(date);
                  }}
                  monthYearPicker={selectedDateType === EDateTypes.MONTHLY}
                  yearPicker={selectedDateType === EDateTypes.YEARLY}
                  dateFormat={dateFormat}
                  fontSize={12}
                  error={dateRangeError}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </FilteringSidePanel>
  );
};

export default DashboardFilters;
